import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import CheckWhiteIcon from "@styles/all-images/icons/checkwhite.svg";
import CloseBlueIcon from "@styles/all-images/icons/closeblue.svg";
import ViewTermsSheetModal from "../TierConfiguration/ViewTermsSheetModal";
import TierConfigurationRejectionReason from "../TierConfiguration/TierConfigurationRejectionReason";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
interface CommodityType {
  id: number;
  title: string;
}

interface DebtorType {
  id: number;
  debtor_type: string;
}

interface PaymentTerm {
  id: number;
  payment_term: string;
}

interface ClientType {
  id: number;
  client_type: string;
}

interface Profession {
  id: number;
  title: string;
}

interface Company {
  id: number;
  name: string;
  trading_as: string;
  commodity_types: CommodityType[];
  administrative_first_name: string;
  administrative_last_name: string;
  administrative_email: string;
  administrative_contact_number: string;
  designation: string;
  facility_required: boolean;
  facility_amount: number;
  debtor_type: number;
  payment_terms: number;
  client_type: number;
  profession: number;
  reason_to_access: string;
}

interface SystemUser {
  id: number;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  name: string;
  user_type: {
    id: number;
    user_type: string;
  };
  profile_picture: string | null;
  permission_profiles: any[];
  is_staff: boolean;
  accept_tc: boolean;
  is_active: boolean;
  status: number;
  companies: Company[];
  created_by: number;
  updated_by: number;
  deleted_by: number | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UserDetails {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  company_name: string;
  trading_as: string;
  commodity_types: CommodityType[];
  administrative_first_name: string;
  administrative_last_name: string;
  administrative_email: string;
  administrative_contact_number: string;
  designation: string;
  facility_required: boolean;
  facility_amount: number;
  debtor_type: DebtorType;
  payment_terms: PaymentTerm;
  client_type: ClientType;
  profession: Profession;
  reason_to_access: string;
  status: number;
  rejection_reason: string | null;
  created_at: string;
  system_user: SystemUser;
  message: string;
  updated_fields: any;
}

const UserManagementDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch<AppThunkDispatch>();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isViewTermsSheetModalOpen, setIsViewTermsSheetModalOpen] =
    useState(false);
  const [isTermsSheetOpen, setIsTermsSheetOpen] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchUserDetails = async (id: string) => {
      try {
        const { authenticatedCall } = actionsHandler("userByID");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: ["profile-update/" + id + "/"],
          }),
        );

        if (response.ok) {
          setUserDetails(response.data);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };

    if (id) {
      fetchUserDetails(id);
    }
  }, [dispatch, id]);

  const handleViewTermsSheet = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTermsSheetOpen(true);
  };

  const handleApprove = async () => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("userByID");
      const response = await dispatch(
        authenticatedCall({
          method: "patch",
          customParams: ["profile-update/" + id + "/"],
          payload: {
            status: 1,
            rejection_reason: null,
          },
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            "Profile update request approved successfully",
        );
        navigate("/user-management/edit-requests");
      }
    } catch (error) {
      console.error("Error approving profile update:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (rejectionReason: string) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("userByID");
      const response = await dispatch(
        authenticatedCall({
          method: "patch",
          customParams: ["profile-update/" + id + "/"],
          payload: {
            status: 2,
            rejection_reason: rejectionReason,
          },
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            "Profile update request rejected successfully",
        );
        setIsRejectionModalOpen(false);
        navigate("/user-management/edit-requests");
      }
    } catch (error) {
      console.error("Error rejecting profile update:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRejectionModal = () => {
    setIsRejectionModalOpen(true);
  };

  const handleCloseTermsSheet = () => {
    setIsTermsSheetOpen(false);
  };

  const handleCloseRejectionModal = () => {
    setIsRejectionModalOpen(false);
  };

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a href="/user-management/edit-requests">
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                User Details
              </h1>
            </div>
            <div className="tw-flex tw-space-x-4">
              <button
                onClick={handleOpenRejectionModal}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border tw-flex tw-items-center tw-gap-2 tw-font-semibold
               tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center tw-text-base tw-text-[#146C94]"
              >
                Reject
                <img
                  src={CloseBlueIcon}
                  alt="Plus Icon"
                  className="tw-w-7 tw-h-7 tw-max-w-7 tw-max-h-7 tw-min-w-7 tw-min-h-7"
                />
              </button>
              <button
                onClick={handleApprove}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2
               tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                Approve
                <img
                  src={CheckWhiteIcon}
                  alt="Plus Icon"
                  className="tw-w-7 tw-h-7 tw-max-w-7 tw-max-h-7 tw-min-w-7 tw-min-h-7"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="tw-p-6 tw-mt-[147px] tw-gap-6 tw-flex-col tw-flex">
        {/* Updated Details Section */}
        <div className="tw-bg-[#ffffff] tw-rounded-2xl tw-shadow-md tw-border tw-border-[#809199]">
          <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-bg-[#ECF1F8] tw-border-b tw-border-[#809199] tw-rounded-t-2xl">
            <h2 className="tw-text-base tw-font-semibold">Updated Details</h2>
          </div>
          <hr />
          <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8 tw-p-8">
            {userDetails?.updated_fields &&
              Object.entries(userDetails.updated_fields).map(([key, value]) => {
                // Format the display value based on data type
                let displayValue = "-";
                if (typeof value === "object" && value !== null) {
                  if (Array.isArray(value)) {
                    displayValue = value.map((item) => item.title).join(", ");
                  } else if ("title" in value) {
                    displayValue = value.title as string;
                  } else if ("debtor_type" in value) {
                    displayValue = value.debtor_type as string;
                  } else if ("payment_term" in value) {
                    displayValue = value.payment_term as string;
                  } else if ("client_type" in value) {
                    displayValue = value.client_type as string;
                  }
                } else if (typeof value === "boolean") {
                  displayValue = value ? "Yes" : "No";
                } else if (value !== null && value !== undefined) {
                  displayValue = String(value);
                }

                // Format the key for display
                const displayKey = key
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");

                return (
                  <div key={key} className="tw-flex tw-flex-col tw-gap-1">
                    <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                      {displayKey}
                    </span>
                    <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                      {displayValue}
                    </h4>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Basic Details Section */}
        <div className="tw-bg-[#ffffff] tw-rounded-2xl tw-shadow-md tw-border tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center tw-p-4">
            <h2 className="tw-text-base tw-font-semibold">Basic Details</h2>
          </div>
          <hr />
          <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8 tw-p-8">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                User Name
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user &&
                userDetails?.system_user?.first_name
                  ? `${userDetails.system_user.first_name} ${userDetails.system_user.last_name}`
                  : userDetails?.system_user?.name || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Email
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.email || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Phone
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.phone || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                User Type
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.user_type?.user_type || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Status
              </span>
              <div className="tw-flex tw-items-center tw-gap-2">
                <span
                  className={`tw-w-2 tw-h-2 tw-rounded-full  tw-bg-[#ECFDF3]`}
                ></span>
                <h4
                  className={`tw-text-base tw-font-semibold tw-text-[#027A48]`}
                >
                  {userDetails?.system_user.status}
                </h4>
              </div>
            </div>
          </div>
        </div>
        {/* Company Details Section */}
        <div className="tw-bg-[#ffffff] tw-rounded-2xl tw-shadow-md tw-border tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center tw-p-4">
            <h2 className="tw-text-base tw-font-semibold">Company Details</h2>
          </div>
          <hr />
          <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8 tw-p-8">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Company Name
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.name || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Trading As
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.trading_as || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Commodity Types
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.commodity_types
                  ?.map((commodity: CommodityType) => commodity.title)
                  ?.join(", ") || "-"}
              </h4>
            </div>
          </div>
        </div>
        {/* Administrative Details Section */}
        <div className="tw-bg-[#ffffff] tw-rounded-2xl tw-shadow-md tw-border tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center tw-p-4">
            <h2 className="tw-text-base tw-font-semibold">
              Administrative Details
            </h2>
          </div>
          <hr />
          <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8 tw-p-8">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Administrative Name
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]
                  ? `${userDetails.system_user.companies[0].administrative_first_name} ${userDetails.system_user.companies[0].administrative_last_name}`
                  : "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Email
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.administrative_email ||
                  "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Contact Number
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]
                  ?.administrative_contact_number || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Designation
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.designation || "-"}
              </h4>
            </div>
          </div>
        </div>
        {/* Other Details Section */}
        <div className="tw-bg-[#ffffff] tw-rounded-2xl tw-shadow-md tw-border tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center tw-p-4">
            <h2 className="tw-text-base tw-font-semibold">Other Details</h2>
          </div>
          <hr />
          <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8 tw-p-8">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Debtor Type
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.debtor_type || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Payment Terms
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.payment_terms || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Client Type
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.client_type || "-"}
              </h4>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                Profession
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails?.system_user.companies[0]?.profession || "-"}
              </h4>
            </div>
          </div>
        </div>
        {isViewTermsSheetModalOpen && (
          <ViewTermsSheetModal
            onClose={() => setIsViewTermsSheetModalOpen(true)}
          />
        )}
        {isTermsSheetOpen && (
          <ViewTermsSheetModal onClose={handleCloseTermsSheet} />
        )}

        <div>
          {isRejectionModalOpen && (
            <TierConfigurationRejectionReason
              onClose={() => setIsRejectionModalOpen(false)}
              onSubmit={handleReject}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementDetails;
